package com.yendo.mx.ui.restaurant

import com.yendo.mx.utils.BackHandler
import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.snapshots.SnapshotStateMap
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.text.style.TextAlign
import com.yendo.mx.models.Producto
import com.yendo.mx.models.OpcionAdicional
import com.yendo.mx.models.GrupoAdicional
import com.yendo.mx.models.CartManager
import com.yendo.mx.utils.formatPrecio
import com.yendo.mx.utils.formatDecimal
import com.yendo.mx.ui.theme.RosaYendo
import kotlinx.coroutines.launch
import kotlin.math.roundToInt

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll

import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import androidx.compose.ui.graphics.FilterQuality

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SelectorAdicionales(
    producto: Producto,
    onDismiss: () -> Unit,
    onConfirmar: (Producto, Double, String, List<OpcionAdicional>, String) -> Unit
) {
    val isDark = isSystemInDarkTheme()
    val colorFondoHoja = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorFondoInferior = if (isDark) Color(0xFF2C2C2E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorFondoControles = if (isDark) Color(0xFF3A3A3C) else Color(0xFFF5F5F5)
        val colorBordeTextField = if (isDark) Color(0xFF3A3A3C) else Color.LightGray

    val scope = rememberCoroutineScope()
    val focusManager = androidx.compose.ui.platform.LocalFocusManager.current
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    val step = remember(producto.tipoVenta, producto.pesoEstimadoKg) {
        if (producto.tipoVenta == "PESO") {
            if (producto.pesoEstimadoKg > 0.0) producto.pesoEstimadoKg else 0.25
        } else {
            1.0
        }
    }
    var modoCompra by remember { mutableStateOf(if (producto.tipoVenta == "PIEZA_POR_PESO") "UNIDAD" else producto.tipoVenta) }
    var cantidad by remember { mutableStateOf(step) }
    var notas by remember { mutableStateOf("") }
    val selecciones = remember { mutableStateMapOf<String, MutableList<OpcionAdicional>>() }
    val gruposExpandidos = remember { mutableStateMapOf<String, Boolean>() }

    val precioUnitarioVigente = remember(cantidad, modoCompra, producto.escalasPrecio) {
        val cantidadYaEnCarritoNormalizada = CartManager.items
            .filter { it.producto.id == producto.id }
            .sumOf { item ->
                if (item.producto.tipoVenta == "PIEZA_POR_PESO") {
                    item.cantidad * item.producto.pesoEstimadoKg
                } else {
                    item.cantidad
                }
            }

        val cantidadActualNormalizada = if (producto.tipoVenta == "PIEZA_POR_PESO") {
            cantidad * producto.pesoEstimadoKg
        } else {
            cantidad
        }

        val cantidadProyectada = cantidadYaEnCarritoNormalizada + cantidadActualNormalizada

        val escalaAplicable = producto.escalasPrecio
            .filter { cantidadProyectada >= it.cantidadMinima.toDouble() && it.precioUnitario > 0.0 }
            .maxByOrNull { it.cantidadMinima }

        val pRaw = if (escalaAplicable != null) {
            val qMax = if (escalaAplicable.cantidadMaxima.toDouble() > 0.0) escalaAplicable.cantidadMaxima.toDouble() else Double.MAX_VALUE
            if (cantidadProyectada > qMax) {
                val costoConMayoreo = qMax * escalaAplicable.precioUnitario
                val costoExcedenteRegular = (cantidadProyectada - qMax) * producto.precio
                (costoConMayoreo + costoExcedenteRegular) / cantidadProyectada
            } else {
                escalaAplicable.precioUnitario
            }
        } else {
            producto.precio
        }
        pRaw
    }

    val precioExtras = selecciones.values.flatten().sumOf { it.precioExtra }

    BackHandler {
        scope.launch { sheetState.hide() }.invokeOnCompletion { onDismiss() }
    }

    val bloqueoSwipeAbajo = remember {
        object : NestedScrollConnection {
            override fun onPostScroll(consumed: Offset, available: Offset, source: NestedScrollSource): Offset {
                return Offset(x = 0f, y = available.y)
            }
        }
    }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        containerColor = colorFondoHoja,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        modifier = Modifier
            .fillMaxWidth()
            .clickable(interactionSource = remember { androidx.compose.foundation.interaction.MutableInteractionSource() }, indication = null) { focusManager.clearFocus() },
        contentWindowInsets = { WindowInsets(0, 0, 0, 0) }
    ) {
        // 🚀 ALTURA POR DEFECTO (0.85) PERO CONTENIDO QUE PERMITE VER TODO
        Box(modifier = Modifier.fillMaxHeight(0.85f).imePadding()) {
            Column(modifier = Modifier.fillMaxSize()) {
                LazyColumn(
                    modifier = Modifier.weight(1f).nestedScroll(bloqueoSwipeAbajo),
                    contentPadding = PaddingValues(bottom = 20.dp)
                ) {
                    item {
                        // 🚀 IMAGEN EN CUADRO PERFECTO (1:1) SIN RECORTES
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .aspectRatio(1f) // 🚀 Formato cuadrado solicitado
                                .background(colorFondoHoja) // 🚀 CAMBIADO: Ahora usa el color del módulo
                        ) {
                            if (producto.foto.isNotEmpty()) {
                                AsyncImage(
                                    model = ImageRequest.Builder(LocalPlatformContext.current)
                                        .data(producto.foto)
                                        .crossfade(true)
                                        .build(),
                                    contentDescription = null,
                                    modifier = Modifier.fillMaxSize(),
                                    contentScale = ContentScale.Fit, // 🚀 FIT: Muestra el producto completo
                                    filterQuality = FilterQuality.High
                                )
                            }
                            IconButton(
                                onClick = { scope.launch { sheetState.hide() }.invokeOnCompletion { onDismiss() } },
                                modifier = Modifier.padding(16.dp).background(Color.Black.copy(alpha = 0.4f), CircleShape).size(36.dp)
                            ) {
                                Icon(Icons.Default.Close, null, tint = Color.White, modifier = Modifier.size(20.dp))
                            }
                        }
                    }

                    item {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text(producto.nombre, fontSize = 24.sp, fontWeight = FontWeight.ExtraBold, color = colorTextoPrincipal)
                            Text(producto.descripcion, color = colorTextoSecundario, fontSize = 14.sp)
                            Spacer(Modifier.height(8.dp))

                            if ((producto.tipoVenta == "PIEZA_POR_PESO" || producto.tipoVenta == "PESO") && producto.pesoEstimadoKg > 0.0) {
                                val pesoEnGramos = (producto.pesoEstimadoKg * 1000).roundToInt()
                                val unitMeta = producto.precioPor.uppercase().trim()
                                val precioReferenciaMayor = producto.precio
                                val precioReferenciaMenor = precioReferenciaMayor / 1000.0

                                Column(
                                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp).background(colorFondoControles.copy(alpha = 0.5f), RoundedCornerShape(8.dp)).padding(10.dp)
                                ) {
                                    val esVolumenRef = unitMeta == "LITRO" || producto.precioPor == "LITRO"
                                    val sufijoRef = if (esVolumenRef) "ml" else "g"
                                    val etiquetaUnidad = if (producto.tipoVenta == "PIEZA_POR_PESO") "unidad" else "porción"
                                    Text(text = "aprox. $pesoEnGramos $sufijoRef / $etiquetaUnidad", fontWeight = FontWeight.Bold, fontSize = 12.sp, color = colorTextoPrincipal)
                                    Row(modifier = Modifier.fillMaxWidth().padding(top = 2.dp), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                                        val etiquetaMayor = if (esVolumenRef) "L" else "kg"
                                        val etiquetaMenor = if (esVolumenRef) "ml" else "g"
                                        Text(text = "$${formatPrecio(precioReferenciaMayor)} / $etiquetaMayor", fontSize = 11.sp, color = colorTextoSecundario)
                                        Text(text = "$${formatDecimal(precioReferenciaMenor, 2)} / $etiquetaMenor", fontSize = 11.sp, color = colorTextoSecundario)
                                    }
                                }
                            }
                        }
                    }

                    if (producto.tipoVenta == "PIEZA_POR_PESO") {
                        item {
                            Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)) {
                                Text("¿Cómo prefieres pedirlo?", fontWeight = FontWeight.Bold, color = colorTextoPrincipal, modifier = Modifier.padding(bottom = 8.dp))
                                Row(modifier = Modifier.fillMaxWidth().height(40.dp).background(colorFondoControles, RoundedCornerShape(8.dp)).padding(2.dp)) {
                                    Box(modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(6.dp)).background(if (modoCompra == "UNIDAD") colorFondoHoja else Color.Transparent).clickable { modoCompra = "UNIDAD" }, contentAlignment = Alignment.Center) { Text("Por unidad", color = if (modoCompra == "UNIDAD") RosaYendo else colorTextoSecundario, fontWeight = FontWeight.Bold, fontSize = 14.sp) }
                                    Box(modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(6.dp)).background(if (modoCompra == "PESO") colorFondoHoja else Color.Transparent).clickable { modoCompra = "PESO" }, contentAlignment = Alignment.Center) { Text("Por peso", color = if (modoCompra == "PESO") RosaYendo else colorTextoSecundario, fontWeight = FontWeight.Bold, fontSize = 14.sp) }
                                }
                            }
                        }
                    }

                    items(items = producto.adicionales, key = { it.nombreGrupo }) { grupo ->
                        SeccionGrupoAdicionales(grupo = grupo, gruposExpandidos = gruposExpandidos, selecciones = selecciones, colorTextoPrincipal = colorTextoPrincipal, colorTextoSecundario = colorTextoSecundario, colorFondoControles = colorFondoControles, colorBordeTextField = colorBordeTextField)
                    }

                    item {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text("¿Alguna instrucción especial?", fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
                            Spacer(Modifier.height(8.dp))
                            OutlinedTextField(value = notas, onValueChange = { notas = it }, modifier = Modifier.fillMaxWidth().height(100.dp), placeholder = { Text("Ej. Sin picante, quitar cebolla...", color = colorTextoSecundario) }, shape = RoundedCornerShape(12.dp), colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeTextField, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal))
                        }
                    }
                }

                val cumpleMinimos = producto.adicionales.all { g -> (selecciones[g.nombreGrupo]?.size ?: 0) >= g.minimoSeleccion }
                Surface(modifier = Modifier.fillMaxWidth(), shadowElevation = 16.dp, color = colorFondoInferior) {
                    Column {
                        if (producto.escalasPrecio.isNotEmpty()) {
                            val cantidadYaEnCarritoNormalizada = CartManager.items.filter { it.producto.id == producto.id }.sumOf { item -> if (item.producto.tipoVenta == "PIEZA_POR_PESO") item.cantidad * item.producto.pesoEstimadoKg else item.cantidad }
                            val cantidadActualNormalizada = if (producto.tipoVenta == "PIEZA_POR_PESO") cantidad * producto.pesoEstimadoKg else cantidad
                            val cantidadProyectada = cantidadYaEnCarritoNormalizada + cantidadActualNormalizada
                            val proximaEscala = producto.escalasPrecio.filter { it.cantidadMinima > cantidadProyectada }.minByOrNull { it.cantidadMinima }
                            val esComercio = CartManager.restauranteActualTipoNegocio == "COMERCIO" || CartManager.restauranteActualTipoNegocio == "TIENDITA"

                            Box(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), contentAlignment = Alignment.Center) {
                                if (proximaEscala != null) {
                                    val faltan = proximaEscala.cantidadMinima - cantidadProyectada
                                    val textoFaltan = if (faltan % 1.0 == 0.0) formatDecimal(faltan, 0) else formatDecimal(faltan, 2)
                                    Text("🔥 ¡Agrega $textoFaltan más para un ${if (esComercio) "precio de mayoreo" else "precio especial"} de $${proximaEscala.precioUnitario}!", color = RosaYendo, fontSize = 12.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
                                } else if (precioUnitarioVigente < producto.precio) {
                                    Text(if (esComercio) "🎉 ¡Felicidades! Tienes precio de mayoreo aplicado" else "🎉 ¡Felicidades! Tienes un descuento aplicado", color = Color(0xFF00BFA5), fontSize = 12.sp, fontWeight = FontWeight.ExtraBold, textAlign = TextAlign.Center)
                                }
                            }
                        }

                        Row(modifier = Modifier.navigationBarsPadding().padding(start = 16.dp, end = 16.dp, bottom = 24.dp, top = 12.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.background(colorFondoControles, RoundedCornerShape(12.dp))) {
                                IconButton(onClick = { if (cantidad > step + 0.001) cantidad = ((cantidad - step) * 1000.0).roundToInt() / 1000.0 }) { Icon(Icons.Default.Remove, null, tint = RosaYendo) }
                                val textoCentro = when (producto.tipoVenta) {
                                    "PESO" -> {
                                        val esVolumen = producto.precioPor == "LITRO"; val sufijoMenor = if (esVolumen) "ml" else "g"; val sufijoMayor = if (esVolumen) "L" else "kg"
                                        val subUnidad = (cantidad * 1000).roundToInt()
                                        if (subUnidad < 1000) "$subUnidad $sufijoMenor" else "${formatDecimal(cantidad, 2)} $sufijoMayor"
                                    }
                                    "PIEZA_POR_PESO" -> if (modoCompra == "PESO") {
                                        val esVolumen = producto.precioPor == "LITRO"; val sufijoMenor = if (esVolumen) "ml" else "g"; val sufijoMayor = if (esVolumen) "L" else "kg"
                                        val subUnidadTotal = (producto.pesoEstimadoKg * cantidad * 1000).roundToInt()
                                        if (subUnidadTotal < 1000) "$subUnidadTotal $sufijoMenor" else "${formatDecimal(subUnidadTotal / 1000.0, 2)} $sufijoMayor"
                                    } else "${cantidad.toInt()} un."
                                    else -> if (cantidad % 1.0 == 0.0) cantidad.toInt().toString() else formatDecimal(cantidad, 2)
                                }
                                Text(text = textoCentro, fontWeight = FontWeight.Black, fontSize = 13.sp, textAlign = TextAlign.Center, color = colorTextoPrincipal, modifier = Modifier.padding(horizontal = 8.dp))
                                IconButton(onClick = { cantidad = ((cantidad + step) * 1000.0).roundToInt() / 1000.0 }) { Icon(Icons.Default.Add, null, tint = RosaYendo) }
                            }

                            Button(
                                onClick = { scope.launch { sheetState.hide() }.invokeOnCompletion { onConfirmar(producto, cantidad, notas, selecciones.values.flatten(), modoCompra) } },
                                modifier = Modifier.weight(1f).height(52.dp), shape = RoundedCornerShape(12.dp), colors = ButtonDefaults.buttonColors(containerColor = RosaYendo, disabledContainerColor = Color.Gray), enabled = cumpleMinimos
                            ) {
                                val totalEstimado = if (producto.tipoVenta == "PIEZA_POR_PESO") (cantidad * producto.pesoEstimadoKg * precioUnitarioVigente) + (precioExtras * cantidad) else (precioUnitarioVigente + precioExtras) * cantidad
                                Text(if (cumpleMinimos) "Agregar • $${formatPrecio(totalEstimado)}" else "Elige opciones", fontWeight = FontWeight.Bold, fontSize = if (cumpleMinimos) 16.sp else 13.sp, color = Color.White)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SeccionGrupoAdicionales(grupo: GrupoAdicional, gruposExpandidos: SnapshotStateMap<String, Boolean>, selecciones: SnapshotStateMap<String, MutableList<OpcionAdicional>>, colorTextoPrincipal: Color, colorTextoSecundario: Color, colorFondoControles: Color, colorBordeTextField: Color) {
    val expandido = gruposExpandidos.getOrPut(grupo.nombreGrupo) { true }
    val listaSeleccionada = selecciones.getOrPut(grupo.nombreGrupo) { mutableStateListOf() }
    val cantidadSeleccionada = listaSeleccionada.size
    LaunchedEffect(cantidadSeleccionada) { if (grupo.limiteSeleccion in 1..cantidadSeleccionada) { kotlinx.coroutines.delay(300); gruposExpandidos[grupo.nombreGrupo] = false } }

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(colorFondoControles.copy(alpha = 0.05f)) // 🚀 Más limpio
                .clickable { gruposExpandidos[grupo.nombreGrupo] = !expandido }
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(grupo.nombreGrupo, fontWeight = FontWeight.Black, fontSize = 18.sp, color = colorTextoPrincipal)
                val subTexto = if (grupo.minimoSeleccion > 0) {
                    if (grupo.minimoSeleccion == grupo.limiteSeleccion) "Obligatorio • Elige ${grupo.limiteSeleccion}"
                    else "Obligatorio • Elige al menos ${grupo.minimoSeleccion}"
                } else {
                    "Opcional • Elige hasta ${grupo.limiteSeleccion}"
                }
                Text(subTexto, color = if (grupo.minimoSeleccion > 0) RosaYendo else colorTextoSecundario, fontSize = 12.sp, fontWeight = FontWeight.Bold)
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (grupo.minimoSeleccion in 1..cantidadSeleccionada) {
                    Icon(Icons.Default.CheckCircle, null, tint = Color(0xFF00BFA5), modifier = Modifier.size(20.dp).padding(end = 4.dp))
                }
                Icon(imageVector = if (expandido) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown, null, tint = colorTextoSecundario)
            }
        }

        AnimatedVisibility(visible = expandido, enter = expandVertically() + fadeIn(), exit = shrinkVertically() + fadeOut()) {
            LazyRow(
                modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                contentPadding = PaddingValues(horizontal = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                items(items = grupo.opciones) { opcion ->
                    ChipAdicional(
                        opcion = opcion,
                        grupo = grupo,
                        listaSeleccionada = listaSeleccionada,
                        puedeAgregarMas = (cantidadSeleccionada < grupo.limiteSeleccion),
                        colorTextoPrincipal = colorTextoPrincipal,
                        colorTextoSecundario = colorTextoSecundario,
                        colorBorde = colorBordeTextField
                    )
                }
            }
        }
    }
}

@Composable
fun ChipAdicional(
    opcion: OpcionAdicional,
    grupo: GrupoAdicional,
    listaSeleccionada: MutableList<OpcionAdicional>,
    puedeAgregarMas: Boolean,
    colorTextoPrincipal: Color,
    colorTextoSecundario: Color,
    colorBorde: Color
) {
    val cantEstaOpcion = listaSeleccionada.count { it.nombre == opcion.nombre }
    val isSelected = cantEstaOpcion > 0
    val isDark = isSystemInDarkTheme() // 🚀 Obtener estado del tema
    val alpha = if (opcion.disponible) 1f else 0.5f
    val tieneFoto = opcion.foto.isNotBlank()

    Surface(
        onClick = {
            if (!opcion.disponible) return@Surface
            val o = opcion.copy(nombre_grupo = grupo.nombreGrupo)
            
            if (grupo.limiteSeleccion == 1) {
                listaSeleccionada.clear()
                listaSeleccionada.add(o)
            } else if (!grupo.permiteRepeticion) {
                if (isSelected) {
                    listaSeleccionada.removeAll { it.nombre == opcion.nombre }
                } else if (puedeAgregarMas) {
                    listaSeleccionada.add(o)
                }
            } else {
                if (puedeAgregarMas) {
                    listaSeleccionada.add(o)
                }
            }
        },
        shape = RoundedCornerShape(if (tieneFoto) 28.dp else 22.dp), // 🚀 Más redondeado si hay foto
        color = if (isSelected) {
            if (isDark) RosaYendo.copy(alpha = 0.15f) else Color.White 
        } else Color.Transparent,
        border = BorderStroke(
            width = if (isSelected) 2.dp else 1.dp,
            color = if (isSelected) RosaYendo else colorBorde.copy(alpha = 0.5f)
        ),
        shadowElevation = 0.dp,
        modifier = Modifier.alpha(alpha).animateContentSize() 
    ) {
        Column(
            modifier = Modifier.padding(
                horizontal = if (tieneFoto) 20.dp else 16.dp, 
                vertical = if (tieneFoto) 14.dp else 12.dp 
            )
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Soporte para iconos
                if (tieneFoto) {
                    AsyncImage(
                        model = ImageRequest.Builder(LocalPlatformContext.current).data(opcion.foto).crossfade(true).build(),
                        contentDescription = null,
                        modifier = Modifier.size(42.dp).clip(CircleShape), 
                        contentScale = ContentScale.Crop
                    )
                    Spacer(Modifier.width(12.dp))
                }

                Column {
                    Text(
                        text = opcion.nombre,
                        fontSize = if (tieneFoto) 16.sp else 15.sp, 
                        fontWeight = if (isSelected) FontWeight.Black else FontWeight.Medium,
                        color = if (isSelected) RosaYendo else colorTextoPrincipal
                    )

                    if (!opcion.disponible) {
                        Text(
                            text = "AGOTADO",
                            fontSize = 11.sp,
                            color = Color.Red,
                            fontWeight = FontWeight.Bold
                        )
                    } else if (opcion.precioExtra > 0) {
                        Text(
                            text = "+ $${formatPrecio(opcion.precioExtra)}",
                            fontSize = if (tieneFoto) 12.sp else 11.sp,
                            color = if (isSelected) RosaYendo.copy(alpha = 0.7f) else colorTextoSecundario
                        )
                    }
                }
            }

            // Lógica de repetición: ABAJO
            if (grupo.permiteRepeticion && isSelected) {
                Spacer(Modifier.height(12.dp))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 4.dp, horizontal = 12.dp)
                ) {
                    // Botón disminuir
                    Surface(
                        onClick = {
                            val idx = listaSeleccionada.indexOfFirst { it.nombre == opcion.nombre }
                            if (idx != -1) listaSeleccionada.removeAt(idx)
                        },
                        shape = CircleShape,
                        color = RosaYendo.copy(alpha = 0.15f),
                        modifier = Modifier.size(28.dp)
                    ) {
                        Icon(Icons.Default.Remove, null, tint = RosaYendo, modifier = Modifier.padding(6.dp))
                    }
                    
                    Text(
                        text = cantEstaOpcion.toString(),
                        modifier = Modifier.padding(horizontal = 12.dp),
                        fontWeight = FontWeight.Black,
                        fontSize = 15.sp,
                        color = RosaYendo
                    )

                    // Botón aumentar
                    Surface(
                        onClick = {
                            if (puedeAgregarMas) {
                                val o = opcion.copy(nombre_grupo = grupo.nombreGrupo)
                                listaSeleccionada.add(o)
                            }
                        },
                        shape = CircleShape,
                        color = RosaYendo.copy(alpha = 0.15f),
                        modifier = Modifier.size(28.dp)
                    ) {
                        Icon(Icons.Default.Add, null, tint = RosaYendo, modifier = Modifier.padding(6.dp))
                    }
                }
            }
        }
    }
}
